import React from 'react'
import styled from 'styled-components'
import {FuegokitThemeTypes, themeGet} from '@fuegokit/react/utils/theme-get'
import sx, {SxProp} from '@fuegokit/react/sx'
import Box, {BoxProps} from '@fuegokit/react/components/Box'
import Text, {TextProps} from '@fuegokit/react/components/Text'

const textColors: Record<Appearance, TextProps['color']> = {
  added: 'text.success',
  default: 'text.default',
  important: 'text.danger',
  primary: 'text.inverse',
  primaryInverted: 'text.brand',
  removed: 'text.danger',
  disabled: 'text.disabled'
}

function getSafeValue(value: string | number = 0) {
  if (typeof value === 'number' && value < 0) {
    return 0
  }

  return value
}
export function formatValue(value?: string | number, max?: number) {
  const safeValue = getSafeValue(value)
  const safeMax = getSafeValue(max)

  if (safeMax && safeMax < safeValue) {
    return `${safeMax}+`
  }

  if (safeValue === Infinity) {
    return '∞'
  }

  return safeValue.toString()
}

export type Appearance = 'added' | 'default' | 'important' | 'primary' | 'primaryInverted' | 'removed' | 'disabled'

export interface AtlasBadgeProps extends BoxProps {
  /**
   * Affects the visual style of the badge.
   */
  appearance?: Appearance

  /**
   * The value displayed within the badge. A `ReactNode` can be provided for
   * custom-formatted numbers. Generally, badge should only be used in cases where you want to represent
   * a number, but you may pass a string such as `Any` in some workflow scenarios.
   *
   */
  children?: number | React.ReactNode | string

  /**
   * The maximum value to display. Defaults to `99`. If the value is 100, and max is 50, "50+" will be displayed.
   * This value should be greater than 0. If set to `false` the original value will be displayed regardless of
   * whether it is larger than the default maximum value.
   */
  max?: number | false

  /**
   * Style customization to apply to the badge. Only `backgroundColor` and `color` are supported.
   */
  style?: Pick<React.CSSProperties, 'backgroundColor' | 'color'>

  /**
   * A `testId` prop is provided for specified elements, which is a unique string that appears as a data attribute `data-testid` in the rendered code, serving as a hook for automated tests
   */
  testId?: string
}

const appearanceObject: {[k: string]: FuegokitThemeTypes} = {
  added: 'colors.background.success.default.[default]',
  default: 'colors.background.neutral.default.[default]',
  important: 'colors.background.danger.default.[default]',
  primary: 'colors.background.brand.bold.default',
  primaryInverted: 'colors.elevation.surface.default.[default]',
  removed: 'colors.background.danger.default.[default]',
  disabled: 'colors.background.neutral.default.[default]'
}

const Wrapper = styled(Box)<AtlasBadgeProps & SxProp>`
  display: inline-flex;
  line-height: 20px;
  // @TODO: move spacing grid to REM units
  padding-inline: 0.375rem;
  border-radius: 8px;
  background-color: ${p => (p.appearance ? themeGet(appearanceObject[p.appearance]) : 'revert')};
  ${sx};
`

/**
 * Provides a drop-in replacement for badges that format numbers in Atlassian products.
 *
 * @TODO: adds an additional `disabled` prop; update component in Figma
 */
const AtlasBadge = ({
  appearance = 'default',
  children = 0,
  max = 99,
  style,
  testId,
  ...props
}: AtlasBadgeProps & SxProp) => {
  return (
    <Wrapper appearance={appearance} {...props}>
      <Text sx={{fontSize: 1, lineHeight: '16px', textAlign: 'center', color: textColors[appearance]}}>
        {typeof children === 'number' && max ? formatValue(children, max) : children}
      </Text>
    </Wrapper>
  )
}

AtlasBadge.displayName = 'AtlasBadge'
export default AtlasBadge

const backgroundColors: Record<Appearance, BoxProps['backgroundColor']> = {
  added: 'colors.background.success.default.[default]',
  default: 'colors.background.neutral.default.[default]',
  important: 'colors.background.danger.bold',
  primary: 'colors.background.brand.bold',
  primaryInverted: 'colors.elevation.surface.default.[default]',
  removed: 'background.danger.default.[default]',
  disabled: 'background.neutral.default.[default]'
}
